import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Zap, Car, ChevronRight, ArrowRight, Gauge, Settings, AlertTriangle, Check, Wrench } from 'lucide-react';
import { useState } from 'react';
import Footer from '../components/Footer';
import { useCanonical } from '../hooks/useCanonical';

const vehicleGuides = [
  {
    brand: "BMW",
    logo: "B",
    color: "#0066B1",
    description: "We tune more BMWs than any other brand. The diesel N47/B47 and petrol B58 engines are some of the best tuning platforms out there. The B58 in particular is ridiculously responsive to software - Stage 1 puts it in M3 territory. Even the humble 320d turns into a genuinely quick car with the right calibration.",
    popularModels: [
      { model: "3 Series (F30/G20)", engine: "320d/330d", gains: "30-50 HP, 50-80 Nm" },
      { model: "5 Series (F10/G30)", engine: "520d/530d", gains: "35-60 HP, 60-100 Nm" },
      { model: "X3/X5", engine: "xDrive30d", gains: "40-60 HP, 70-100 Nm" },
      { model: "M140i/M240i", engine: "B58 3.0T", gains: "50-80 HP, 80-120 Nm" }
    ],
    ecuTypes: ["Bosch EDC17", "Bosch MEVD17", "Bosch MG1"],
    tuningNotes: [
      "The B47 diesel (2014+) tunes better and is more reliable than the earlier N47 - no timing chain dramas",
      "B58 3.0 turbo petrol is one of the best engines BMW has ever made. Stage 1 gives 380-420 HP from a stock 326",
      "Most BMW ECUs up to 2018 can be done through the OBD port. Newer G-series may need bench work",
      "If you're going Stage 2 on the turbo models, budget for an intercooler. The stock one heat-soaks badly"
    ],
    commonServices: ["Stage 1 Performance", "Stage 2 Performance", "DPF Delete", "EGR Delete", "AdBlue Delete"],
    specialConsiderations: "BMW's 2019+ models have tighter ECU security. We can still do them, but bench mode reading is usually required instead of OBD."
  },
  {
    brand: "Mercedes-Benz",
    logo: "M",
    color: "#00ADEF",
    description: "Mercedes makes excellent engines that are tuned quite conservatively from the factory. The OM651 and OM654 diesels have plenty of headroom. AMG models respond well too, but the real volume for us is the C220d and E220d crowd - people who want their commuter to feel less sluggish without spending a fortune.",
    popularModels: [
      { model: "C-Class (W205)", engine: "C220d/C300d", gains: "30-50 HP, 50-90 Nm" },
      { model: "E-Class (W213)", engine: "E220d/E350d", gains: "35-60 HP, 60-100 Nm" },
      { model: "GLC/GLE", engine: "300d/350d", gains: "40-70 HP, 70-120 Nm" },
      { model: "AMG C43/E53", engine: "M276/M256", gains: "50-80 HP, 80-130 Nm" }
    ],
    ecuTypes: ["Bosch EDC17", "Bosch MD1", "Siemens SIM271"],
    tuningNotes: [
      "The OM654 diesel is a strong engine that handles increased power without complaints",
      "BlueTEC AdBlue systems on Mercedes are some of the most expensive to repair when they fail - delete makes sense for off-road vehicles",
      "AMG models already have decent calibrations from the factory, so gains are more modest percentage-wise but still very noticeable",
      "Sprinters are our bread and butter for fleet work - economy tunes save serious money on delivery fleets"
    ],
    commonServices: ["Stage 1 Performance", "AdBlue Delete", "DPF Delete", "Start-Stop Disable", "Speed Limiter Removal"],
    specialConsiderations: "Mercedes vehicles with 48V mild hybrid systems need careful handling. The ECU and ISG module have to play nicely together after tuning."
  },
  {
    brand: "Volkswagen / Audi",
    logo: "V",
    color: "#001E50",
    description: "VW Group makes some of the most tuning-friendly engines on the planet. The 2.0 TDI is probably the most-tuned diesel engine in existence, and the EA888 TSI platform has been pushed to 600+ HP on stock internals by people crazier than us. We love working on these - the ECUs are well-documented and the engines have massive headroom.",
    popularModels: [
      { model: "Golf/A3 2.0 TDI", engine: "EA288", gains: "30-50 HP, 60-100 Nm" },
      { model: "Passat/A4 2.0 TDI", engine: "EA288", gains: "35-55 HP, 70-110 Nm" },
      { model: "Golf R/S3", engine: "EA888 2.0 TSI", gains: "50-80 HP, 80-120 Nm" },
      { model: "RS3/TTRS", engine: "2.5 TFSI", gains: "60-100 HP, 100-150 Nm" }
    ],
    ecuTypes: ["Bosch EDC17", "Bosch MED17", "Siemens Simos"],
    tuningNotes: [
      "The EA888 Gen3 is one of the most tuneable petrol engines ever made. Period.",
      "Always do the DSG tune alongside the engine tune on automatics - the stock DSG calibration is painfully hesitant and ruins the experience",
      "The P2015 intake manifold flap code on 2.0 TDI is so common we could probably remove it in our sleep. It's a $10 software fix vs $500 at the dealer",
      "Golf R / S3 owners: Stage 1 turns a quick car into a genuinely fast car. Stage 2 with a downpipe puts you in RS3 territory"
    ],
    commonServices: ["Stage 1 Performance", "Stage 2 Performance", "DSG Tuning", "DPF Delete", "EGR Delete", "Pop & Bang"],
    specialConsiderations: "VW Group vehicles share ECU platforms across brands. A Golf tune often applies to the Seat Leon, Skoda Octavia, and Audi A3 with the same engine."
  },
  {
    brand: "Ford",
    logo: "F",
    color: "#003478",
    description: "Ford's EcoBoost petrols and TDCi/EcoBlue diesels are hugely popular for tuning. The Ranger is easily our most-tuned Ford - work truck owners love the extra torque for towing. Transit vans are a close second, mostly fleet operators looking to save on fuel. The Focus ST/RS crowd keeps us busy on the performance side.",
    popularModels: [
      { model: "Focus ST/RS", engine: "2.3 EcoBoost", gains: "40-70 HP, 70-120 Nm" },
      { model: "Fiesta ST", engine: "1.5 EcoBoost", gains: "30-50 HP, 50-80 Nm" },
      { model: "Ranger/Everest", engine: "3.2 TDCi/2.0 BiTurbo", gains: "40-60 HP, 80-120 Nm" },
      { model: "Transit", engine: "2.0 EcoBlue", gains: "30-50 HP, 60-100 Nm" }
    ],
    ecuTypes: ["Bosch EDC17", "Bosch MED17", "Continental EMS"],
    tuningNotes: [
      "The 3.2 TDCi Ranger has chronic DPF problems in work truck applications - we do more Ranger DPF deletes than any other single model",
      "EcoBoost engines are factory-detuned with lots of headroom. A Stage 1 makes a massive difference on the Focus ST",
      "Transit fleets: economy tunes consistently save 8-12% on fuel. We've had fleet managers tell us it was their best decision of the year",
      "The 2.0 BiTurbo Ranger (2019+) responds even better than the old 3.2 to tuning"
    ],
    commonServices: ["Stage 1 Performance", "Stage 2 Performance", "DPF Delete", "EGR Delete", "Pop & Bang (EcoBoost)"],
    specialConsiderations: "Ford's newer PCM security can be tricky. Some 2020+ models need special protocols for reading - check with your tool manufacturer before ordering."
  },
  {
    brand: "Volvo",
    logo: "V",
    color: "#003057",
    description: "Volvo's Drive-E engines are quietly excellent for tuning. The D4 and D5 diesels have a lot of untapped potential, and the T5/T6 petrols respond beautifully to more boost and fuel. Volvo owners tend to be surprised by how much difference a tune makes - these engines are more conservatively mapped than most people realise.",
    popularModels: [
      { model: "V60/S60 D4", engine: "D4204T", gains: "25-45 HP, 50-80 Nm" },
      { model: "XC60/XC90 D5", engine: "D5244T", gains: "35-55 HP, 60-100 Nm" },
      { model: "S60/V60 T5", engine: "B5204T", gains: "40-60 HP, 60-100 Nm" },
      { model: "Polestar Models", engine: "T6 Twin Scroll", gains: "30-50 HP, 50-80 Nm" }
    ],
    ecuTypes: ["Bosch EDC17", "Denso", "Continental"],
    tuningNotes: [
      "The D5 twin-turbo diesel is a belter of an engine. It pulls like a freight train after a Stage 1 and sounds great doing it",
      "Drive-E platform engines are overbuilt - they handle more power without breaking a sweat",
      "Polestar Engineered models already have a factory-optimised tune, so the extra gains over that baseline are more modest",
      "DPF problems on Volvo diesels are common in city-driven XC60s and XC90s. Same story as everyone else - not enough highway miles"
    ],
    commonServices: ["Stage 1 Performance", "DPF Delete", "EGR Delete", "Speed Limiter Removal", "AdBlue Delete"],
    specialConsiderations: "Volvo's CEM (Central Electronic Module) sometimes needs a coordinated update when the ECU calibration changes significantly. We'll advise if this applies to your model."
  },
  {
    brand: "Toyota / Lexus",
    logo: "T",
    color: "#EB0A1E",
    description: "Toyota builds some of the most reliable engines on the planet. The 1GD-FTV in the Hilux and Prado is an absolute unit - and it tunes brilliantly. The engine itself rarely causes problems, but the emissions equipment (DPF, EGR) is a constant headache for people who use these trucks the way they're meant to be used: off-road, on farms, and loaded up.",
    popularModels: [
      { model: "Hilux/Fortuner", engine: "1GD-FTV 2.8", gains: "35-55 HP, 70-110 Nm" },
      { model: "Land Cruiser 200", engine: "1VD-FTV V8", gains: "40-60 HP, 80-120 Nm" },
      { model: "Land Cruiser Prado", engine: "1GD-FTV", gains: "35-50 HP, 60-100 Nm" },
      { model: "Lexus IS/GS", engine: "2AD-FTV", gains: "25-40 HP, 50-80 Nm" }
    ],
    ecuTypes: ["Denso", "Continental"],
    tuningNotes: [
      "The 1GD-FTV is conservatively mapped from the factory - Toyota left a lot on the table and it shows in the gains we get",
      "Hilux and Prado are our most popular Toyota models by far. A tune plus DPF delete on a farm truck transforms it",
      "Toyota Denso ECUs almost always need bench reading - OBD tuning isn't an option. Your workshop needs to pull the ECU",
      "The Land Cruiser 200 V8 diesel is an animal after a Stage 1. 780 Nm of torque makes towing feel effortless"
    ],
    commonServices: ["Stage 1 Performance", "DPF Delete", "EGR Delete", "Speed Limiter Removal"],
    specialConsiderations: "Toyota and Lexus Denso ECUs require bench mode reading - the ECU must be physically removed from the vehicle. Plan for this when booking workshop time."
  },
  {
    brand: "Nissan",
    logo: "N",
    color: "#C3002F",
    description: "Nissan's diesel truck range is big business for us - the Navara NP300 is a solid tuning platform and the Patrol Y62 has a loyal following. On the petrol side, the GT-R needs no introduction. We've done GT-R files that made over 600 HP, but most of our Nissan work is Navaras and Patrols wanting more towing grunt and fewer DPF headaches.",
    popularModels: [
      { model: "Navara NP300", engine: "YS23 2.3 dCi", gains: "30-50 HP, 60-100 Nm" },
      { model: "Patrol Y62", engine: "VK56VD V8", gains: "30-50 HP, 50-80 Nm" },
      { model: "Pathfinder", engine: "YD25 2.5 dCi", gains: "25-45 HP, 50-90 Nm" },
      { model: "GT-R R35", engine: "VR38DETT", gains: "60-100+ HP, 80-130 Nm" }
    ],
    ecuTypes: ["Denso", "Bosch EDC17", "Continental"],
    tuningNotes: [
      "The Navara NP300 twin-turbo diesel tunes very well - it's a different vehicle with a Stage 1, especially when towing",
      "GT-R tuning is a different world entirely. If you're going big power, talk to us about your hardware first",
      "The Patrol Y62 petrol V8 is thirsty, but an economy tune can take the edge off without losing the grunt you bought it for",
      "YD25 engine in older Navaras and Pathfinders benefits massively from DPF delete for off-road use"
    ],
    commonServices: ["Stage 1 Performance", "DPF Delete", "EGR Delete", "Speed Limiter Removal", "Launch Control (GT-R)"],
    specialConsiderations: "The GT-R's AWD system and dual-clutch transmission add complexity. We need to know your exact build spec for any GT-R file over Stage 1."
  },
  {
    brand: "Hyundai / Kia / Genesis",
    logo: "H",
    color: "#002C5F",
    description: "Korean diesels have come a long way and the 2.2 CRDi is a solid, reliable engine that tunes well. But the real stars are the performance models - the i30 N and Stinger GT have proper enthusiast followings and respond seriously well to ECU work. The aftermarket support is growing fast.",
    popularModels: [
      { model: "i30 N / Veloster N", engine: "2.0T Theta II", gains: "40-60 HP, 60-100 Nm" },
      { model: "Santa Fe/Sorento", engine: "2.2 CRDi", gains: "30-50 HP, 60-100 Nm" },
      { model: "Stinger GT", engine: "3.3 T-GDi V6", gains: "50-80 HP, 80-120 Nm" },
      { model: "Tucson/Sportage", engine: "2.0 CRDi", gains: "25-45 HP, 50-80 Nm" }
    ],
    ecuTypes: ["Bosch EDC17", "Kefico", "Continental"],
    tuningNotes: [
      "The i30 N / Veloster N 2.0T is an absolute bargain for the money, and a Stage 1 makes it genuinely quick",
      "The 2.2 CRDi diesel in the Santa Fe and Sorento is a workhorse. It handles more power without fuss",
      "Stinger GT 3.3 V6 twin-turbo has huge untapped potential. Stage 1 puts it firmly in German sports sedan territory",
      "Korean vehicles often share ECU platforms across brands - a Tucson tune applies to the Sportage with the same engine"
    ],
    commonServices: ["Stage 1 Performance", "Stage 2 Performance", "DPF Delete", "EGR Delete", "N-Mode Enhancements"],
    specialConsiderations: "Some newer Hyundai/Kia models use encrypted ECUs that require specific tools to read. Check compatibility before committing to a tool purchase."
  }
];

const VehicleBrandGuidesPage = () => {
  useCanonical('https://ecuflashservice.com/vehicle-tuning-guides');
  const [selectedBrand, setSelectedBrand] = useState(null);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Vehicle-Specific ECU Tuning Guides - All Major Brands",
    "description": "Comprehensive ECU tuning guides for BMW, Mercedes, Volkswagen, Audi, Ford, Volvo, Toyota, Nissan, and more. Model-specific information and expected gains.",
    "author": {
      "@type": "Organization",
      "name": "ECU Flash Service"
    },
    "publisher": {
      "@type": "Organization",
      "name": "ECU Flash Service",
      "logo": {
        "@type": "ImageObject",
        "url": "https://ecuflashservice.com/favicon-512.png"
      }
    },
    "datePublished": "2024-01-01",
    "dateModified": new Date().toISOString().split('T')[0],
    "mainEntityOfPage": "https://ecuflashservice.com/vehicle-tuning-guides",
    "image": "https://ecuflashservice.com/favicon-512.png"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://ecuflashservice.com" },
      { "@type": "ListItem", "position": 2, "name": "Vehicle Tuning Guides", "item": "https://ecuflashservice.com/vehicle-tuning-guides" }
    ]
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>ECU Tuning by Car Brand | BMW Mercedes VW Audi Ford Guides | 2026</title>
        <meta name="description" content="Vehicle-specific ECU tuning guides. BMW 320d 330d tuning, Mercedes C220d remap, VW Golf GTI tune, Audi S3 Stage 1. Expected power gains for each model." />
        <meta name="keywords" content="BMW ECU tuning, Mercedes remap, VW Golf tune, Audi tuning guide, Ford EcoBoost tune, car specific tuning, brand tuning guide" />
        <meta property="og:title" content="Vehicle-Specific ECU Tuning Guides" />
        <meta property="og:description" content="Comprehensive tuning guides for BMW, Mercedes, VW, Audi, Ford, and more." />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://ecuflashservice.com/favicon-512.png" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-white/10 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-[#1A1A1A] border border-white/10 flex items-center justify-center">
                <Zap className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <span className="font-heading text-lg font-bold uppercase tracking-wide text-white">ECU Flash Service</span>
            </Link>
            <Link to="/" className="btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-4 py-2 text-sm hover:bg-white transition">
              <span>Get Started</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-[#0A0A0A] to-[#050505]">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] border border-[#00F0FF]/30 mb-6">
            <Car className="w-4 h-4 text-[#00F0FF]" />
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00F0FF]">Brand Guides</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase mb-4">
            <span className="text-white">Vehicle Tuning</span>{' '}
            <span className="text-[#00F0FF]">Guides</span>
          </h1>
          <p className="text-[#888] text-lg mb-8 max-w-2xl mx-auto">
            We've tuned thousands of vehicles across every major brand. Here's what we've learned about each one - the good, the bad, and the surprisingly tuneable.
          </p>
        </div>
      </section>

      {/* Brand Grid */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {vehicleGuides.map((guide, index) => (
              <button
                key={index}
                onClick={() => setSelectedBrand(selectedBrand === index ? null : index)}
                className={`p-6 border transition-all ${
                  selectedBrand === index 
                    ? 'bg-[#0A0A0A] border-[#00F0FF]' 
                    : 'bg-[#0A0A0A] border-white/10 hover:border-white/30'
                }`}
                data-testid={`brand-btn-${guide.brand.toLowerCase().replace(/[\s\/]/g, '-')}`}
              >
                <div 
                  className="w-12 h-12 mx-auto mb-3 flex items-center justify-center font-heading text-2xl font-bold"
                  style={{ backgroundColor: `${guide.color}20`, color: guide.color }}
                >
                  {guide.logo}
                </div>
                <h3 className="font-heading text-sm font-bold uppercase tracking-tight text-white text-center">
                  {guide.brand}
                </h3>
              </button>
            ))}
          </div>

          {/* Selected Brand Details */}
          {selectedBrand !== null && (
            <div className="bg-[#0A0A0A] border border-[#00F0FF]/30 p-8 mb-12 animate-fadeIn">
              <div className="flex items-start gap-6 mb-6">
                <div 
                  className="w-16 h-16 flex-shrink-0 flex items-center justify-center font-heading text-3xl font-bold"
                  style={{ backgroundColor: `${vehicleGuides[selectedBrand].color}20`, color: vehicleGuides[selectedBrand].color }}
                >
                  {vehicleGuides[selectedBrand].logo}
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-2">
                    {vehicleGuides[selectedBrand].brand} Tuning Guide
                  </h2>
                  <p className="text-[#888] leading-relaxed">
                    {vehicleGuides[selectedBrand].description}
                  </p>
                </div>
              </div>

              {/* Popular Models */}
              <div className="mb-6">
                <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-[#00F0FF] mb-4">
                  Popular Models & Expected Gains
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {vehicleGuides[selectedBrand].popularModels.map((model, i) => (
                    <div key={i} className="bg-[#080808] border border-white/10 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-white">{model.model}</h4>
                        <span className="text-xs text-[#888] bg-[#1A1A1A] px-2 py-1">{model.engine}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Gauge className="w-4 h-4 text-[#00F0FF]" />
                        <span className="text-sm text-[#00F0FF] font-semibold">{model.gains}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ECU Types */}
              <div className="mb-6">
                <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-[#00F0FF] mb-4">
                  Common ECU Types
                </h3>
                <div className="flex flex-wrap gap-2">
                  {vehicleGuides[selectedBrand].ecuTypes.map((ecu, i) => (
                    <span key={i} className="px-3 py-2 bg-[#1A1A1A] border border-white/10 text-sm text-[#888]">
                      {ecu}
                    </span>
                  ))}
                </div>
              </div>

              {/* Tuning Notes */}
              <div className="mb-6">
                <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-[#00F0FF] mb-4">
                  What We've Learned
                </h3>
                <ul className="space-y-2">
                  {vehicleGuides[selectedBrand].tuningNotes.map((note, i) => (
                    <li key={i} className="flex items-start gap-2 text-[#888] text-sm">
                      <Check className="w-4 h-4 text-[#00F0FF] flex-shrink-0 mt-0.5" />
                      {note}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Services */}
              <div className="mb-6">
                <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-[#00F0FF] mb-4">
                  Available Services
                </h3>
                <div className="flex flex-wrap gap-2">
                  {vehicleGuides[selectedBrand].commonServices.map((service, i) => (
                    <span key={i} className="px-3 py-2 bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-sm text-[#00F0FF]">
                      {service}
                    </span>
                  ))}
                </div>
              </div>

              {/* Special Considerations */}
              <div className="bg-[#FF003C]/10 border border-[#FF003C]/30 p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-[#FF003C] flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-bold uppercase tracking-wider text-[#FF003C] mb-1">
                      Heads Up
                    </h4>
                    <p className="text-sm text-[#888]">
                      {vehicleGuides[selectedBrand].specialConsiderations}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* General Information */}
          <div className="bg-[#0A0A0A] border border-white/10 p-8 mb-12">
            <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-6">
              How Much Power Can You Expect?
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6 mb-6">
              <div className="p-4 bg-[#080808] border border-white/10">
                <h3 className="font-heading text-lg font-bold uppercase text-[#00F0FF] mb-2">Turbocharged Diesel</h3>
                <p className="text-[#888] text-sm mb-2">The biggest gains, every time. Modern common-rail diesels have headroom the manufacturers deliberately leave on the table. 20-40% more power is standard.</p>
                <div className="text-xs text-[#00F0FF]">Typical Stage 1: +30-60 HP, +60-120 Nm</div>
              </div>
              <div className="p-4 bg-[#080808] border border-white/10">
                <h3 className="font-heading text-lg font-bold uppercase text-[#00F0FF] mb-2">Turbocharged Petrol</h3>
                <p className="text-[#888] text-sm mb-2">Excellent gains, especially on direct-injection platforms. The turbo is already there doing the work - we just let it do more of it.</p>
                <div className="text-xs text-[#00F0FF]">Typical Stage 1: +40-80 HP, +60-100 Nm</div>
              </div>
              <div className="p-4 bg-[#080808] border border-white/10">
                <h3 className="font-heading text-lg font-bold uppercase text-[#00F0FF] mb-2">Naturally Aspirated</h3>
                <p className="text-[#888] text-sm mb-2">We'll be straight with you - without a turbo to exploit, the gains are modest. But the throttle response improvement alone is worth it for most people.</p>
                <div className="text-xs text-[#00F0FF]">Typical: +5-15 HP, noticeably sharper response</div>
              </div>
            </div>

            <p className="text-[#888] text-sm">
              <strong className="text-white">Real talk:</strong> These are conservative estimates based on what we see across thousands of tunes. Your actual results depend on the specific engine, its condition, fuel quality, and ambient temperature. We'd rather under-promise and over-deliver.
            </p>
          </div>
        </div>
      </section>

      {/* Ad Placement */}
      <section className="py-8 px-6">
        <div className="container mx-auto max-w-4xl">
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-[#0A0A0A]">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-4">Don't See Your Vehicle?</h2>
          <p className="text-[#888] mb-6">We support just about everything with an ECU. Drop us a message with your vehicle details and we'll let you know what's possible.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/" className="btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-6 py-3 hover:bg-white transition inline-flex items-center justify-center gap-2">
              <span>Start Your Order</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="btn-skew border border-white/30 text-white font-bold uppercase tracking-wider px-6 py-3 hover:border-[#00F0FF] transition">
              <span>Contact Us</span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default VehicleBrandGuidesPage;
